import React, { FC, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Button, IconButton } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { IUpdatePreferencesRequestDTO, NotificationsType } from 'src/api/dtos';
import { CheckFilledIcon, CloseIcon, Popper, ResetIcon, Tooltip, WarningFilledIcon } from 'src/shared/components';
import { FeaturesEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithFeatures, ComponentWithPermissions } from 'src/shared/hocs';
import { useFeatures, useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';
import { updatePreferences } from 'src/store/user/user.actions';

import { Notification } from './Notification';
import './NotificationsForm.styles.scss';

interface Props {
  resetButtonPlacement?: 'top' | 'bottom';
  compact?: boolean;
  showLabels?: boolean;
  onResetSubmit?: () => void;
  onReset?: () => void;
}

const notificationsKeys: Array<keyof NotificationsType> = [
  'absenceCalendarInvites',
  'smsScheduleChanges',
  'emailScheduleChanges',
  'emailShiftSwap',
  'emailAbsenceRequests',
  'emailShiftOffer',
];

export const NotificationsForm: FC<Props> = ({
  resetButtonPlacement = 'top',
  compact,
  showLabels,
  onResetSubmit,
  onReset,
}) => {
  const { t } = useTranslate();
  const dispatch = useThunkDispatch();
  const { info, settings, preferences, defaultPreferences, submittingPreferences, loadingPreferences } = useSelector(
    (state: RootState) => state.user,
  );

  const defaultNotifications = pick<NotificationsType>(defaultPreferences, notificationsKeys);
  const notifications = pick<NotificationsType>(preferences, notificationsKeys);

  const [showDefault, setShowDefault] = useState<boolean>(false);
  const [resetConfirmEl, setResetConfirmEl] = useState<HTMLElement | null>(null);

  const hasAllFeatures = useFeatures([FeaturesEnums.AbsenceRequest, FeaturesEnums.ShiftSwapping]);
  const lineVerticalSpacing = compact ? 'my-3' : 'notifications-vertical-large-spacing';
  const currentNotifications = showDefault ? defaultNotifications : notifications;

  const handleReset = () => {
    if (onReset) {
      onReset();
    }

    setShowDefault(true);
  };

  const handleResetSubmit = async () => {
    if (onResetSubmit) {
      onResetSubmit();
    }

    try {
      await dispatch(updatePreferences({ ...cloneDeep(defaultNotifications) }));
      toast.info(t('settings.successYourNotificationsWereResetSuccessfully'), { type: toast.TYPE.SUCCESS });
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), { type: toast.TYPE.ERROR });
    } finally {
      setShowDefault(false);
    }
  };

  const handleSubmit = async (key: keyof IUpdatePreferencesRequestDTO, value: boolean) => {
    try {
      await dispatch(updatePreferences({ [key]: value }));
      toast.info(t('settings.successYourNotificationsWereUpdatedSuccessfully'), { type: toast.TYPE.SUCCESS });
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), { type: toast.TYPE.ERROR });
    }
  };

  return (
    <>
      {(settings?.isScheduleChangeNotificationEnabled || hasAllFeatures) && (
        <>
          <div>
            <div className="d-flex align-items-center">
              <h5 className="mb-0">{t('settings.emailNotifications')}</h5>
              {!info?.email && (
                <Tooltip
                  placement="bottom-start"
                  title={
                    <span>
                      <span className="fw-medium me-1">{t('global.headsUp')}</span>{' '}
                      {t('settings.updateYourEmailToBeAbleToReceiveEmailNotifications')}
                    </span>
                  }
                >
                  <IconButton className="p-0 ms-2" size="small" color="info">
                    <WarningFilledIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div>
              <ComponentWithFeatures features={FeaturesEnums.AbsenceRequest}>
                <Notification
                  title={t('settings.absenceRequests')}
                  message={
                    showLabels
                      ? t(
                          'settings.controlWhetherYouReceiveNotificationsViaEmailRegardingTheStatusOfYourAbsenceRequests',
                        )
                      : undefined
                  }
                  disabled={!info?.email || showDefault}
                  toggle={currentNotifications.emailAbsenceRequests}
                  onToggle={(value) => handleSubmit('emailAbsenceRequests', value)}
                />
              </ComponentWithFeatures>
              <ComponentWithFeatures features={FeaturesEnums.ShiftSwapping}>
                <Notification
                  title={t('settings.shiftSwap')}
                  message={
                    showLabels
                      ? t('settings.controlWhetherYouReceiveNotificationsViaEmailRegardingShiftSwaping')
                      : undefined
                  }
                  disabled={!info?.email || showDefault}
                  toggle={currentNotifications.emailShiftSwap}
                  onToggle={(value) => handleSubmit('emailShiftSwap', value)}
                />
              </ComponentWithFeatures>
              <ComponentWithPermissions permissions={PermissionsEnums.FEATURE_SHIFT_OFFER}>
                <Notification
                  title={t('calendar.shiftOffer')}
                  message={
                    showLabels ? t('calendar.controlWhetherYouReceiveNotificationsViaEmailRegardingShift') : undefined
                  }
                  disabled={!info?.email || showDefault}
                  toggle={currentNotifications.emailShiftOffer}
                  onToggle={(value) => handleSubmit('emailShiftOffer', value)}
                />
              </ComponentWithPermissions>
              {settings?.isScheduleChangeNotificationEnabled && (
                <Notification
                  title={t('settings.scheduleChanges')}
                  message={
                    showLabels
                      ? t('settings.controlWhetherYouReceiveNotificationsViaEmailRegardingScheduleChanges')
                      : undefined
                  }
                  disabled={!info?.email || showDefault}
                  toggle={currentNotifications.emailScheduleChanges}
                  onToggle={(value) => handleSubmit('emailScheduleChanges', value)}
                />
              )}
            </div>
          </div>
          <div className={`border-bottom ${lineVerticalSpacing}`} />
        </>
      )}
      {settings?.isScheduleChangeNotificationEnabled && (
        <>
          <div>
            <div className="d-flex">
              <h5 className="mb-0">{t('settings.smsNotifications')}</h5>
              {!info?.cellPhone && (
                <Tooltip
                  placement="bottom-start"
                  title={
                    <span>
                      <span className="fw-medium me-1">{t('global.headsUp')}</span>{' '}
                      {t('settings.updateYourPhoneNumberToBeAbleToReceiveNotificationsViaSMS')}
                    </span>
                  }
                >
                  <IconButton className="p-0 ms-2" size="small" color="info">
                    <WarningFilledIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <div>
              <Notification
                title={t('settings.scheduleChanges')}
                message={
                  showLabels
                    ? t('settings.controlWhetherYouReceiveNotificationsViaSMSRegardingScheduleChanges')
                    : undefined
                }
                disabled={!info?.cellPhone || showDefault}
                toggle={currentNotifications.smsScheduleChanges}
                onToggle={(value) => handleSubmit('smsScheduleChanges', value)}
              />
            </div>
          </div>
          <div className={`border-bottom ${lineVerticalSpacing}`} />
        </>
      )}
      <ComponentWithFeatures features={FeaturesEnums.AbsenceRequest}>
        <div>
          <Notification
            title={t('settings.absenceCalendarInvites')}
            message={showLabels ? t('settings.controlWhetherYouReceiveAbsenceCalendarInvites') : undefined}
            disabled={showDefault}
            toggle={currentNotifications.absenceCalendarInvites}
            onToggle={(value) => handleSubmit('absenceCalendarInvites', value)}
            titleTag="h5"
          />
        </div>
      </ComponentWithFeatures>
      <div className="mt-5 d-flex align-items-center justify-content-between">
        {showDefault && (
          <div className="d-flex">
            <Button className="me-2" color="secondary" startIcon={<CloseIcon />} onClick={() => setShowDefault(false)}>
              {t('global.cancel')}
            </Button>
            <div>
              <Popper
                id="notifications-reset-default"
                anchorEl={resetConfirmEl}
                submitText={t('global.yesImSure')}
                closeText={t('global.no')}
                onSubmit={handleResetSubmit}
                onClose={() => {
                  setResetConfirmEl(null);
                  setShowDefault(false);
                }}
              >
                <p className="small">{t('settings.areYouSureYouWantToResetYourNotifications')}</p>
              </Popper>
              <LoadingButton
                aria-describedby="notifications-reset-default"
                startIcon={<CheckFilledIcon />}
                variant="contained"
                loadingPosition="start"
                disabled={loadingPreferences || submittingPreferences}
                loading={submittingPreferences}
                onClick={(e) => setResetConfirmEl(resetConfirmEl ? null : e.currentTarget)}
              >
                {t('global.applyChanges')}
              </LoadingButton>
            </div>
          </div>
        )}
        {(settings?.isScheduleChangeNotificationEnabled || hasAllFeatures) &&
          defaultPreferences &&
          resetButtonPlacement === 'bottom' && (
            <div className={`notifications-reset-button ${resetButtonPlacement}`}>
              <Button
                id="reset-to-default"
                className="mt-2 mt-lg-0"
                startIcon={<ResetIcon />}
                color="secondary"
                onClick={handleReset}
                disabled={showDefault}
              >
                {t('settings.resetToDefault')}
              </Button>
            </div>
          )}
      </div>
      {(settings?.isScheduleChangeNotificationEnabled || hasAllFeatures) &&
        defaultPreferences &&
        resetButtonPlacement === 'top' && (
          <div className={`notifications-reset-button ${resetButtonPlacement}`}>
            <Button
              id="reset-to-default"
              className="mt-2 mt-lg-0"
              startIcon={<ResetIcon />}
              color="secondary"
              onClick={handleReset}
              disabled={showDefault}
            >
              {t('settings.resetToDefault')}
            </Button>
          </div>
        )}
    </>
  );
};
